import template from './voyo_timeline.html';
import { AdBreak } from '@core/libs/voyo_ads';

class VoyoTimeline extends HTMLElement {
    // If true, the timeline is disabled and cannot be interacted with. This can be set via setDisabled method
    // Used for ad skipping prevention in catchup.
    private disabled = false;

    private _startTime = 0;
    private _endTime = 0;
    private _currentTime = 0;

    private progressBarElement: HTMLInputElement|null = null;
    private markersElement: HTMLElement|null = null;
    private adBreaks: Array<AdBreak> = [];
    private currentTimeElement: HTMLElement|null = null;
    private durationElement: HTMLElement|null = null;

    // If false, the duration display will be hidden and replaced with '--:--'
    // Can be set via setDurationDisplay method
    private showDuration = true;
    private thumbnailListenersRegistered = false;

    // Position the user clicked or dragged to, we remember it until the debounced seek runs. 
    private pendingSeekTime: number|null = null;

    // Default seek handler, can be overridden by registerSeekHandler.
    // We use a debounced version of this handler to avoid excessive seeking when dragging the timeline.
    private seekHandler = (time: number) => app.voyoVideo.setVideoPlayback(time);
    private debouncedSeek?: () => void;

    connectedCallback(): void {
        this.innerHTML = template;

        this.progressBarElement = this.querySelector('.timeline_progress');
        if (this.progressBarElement) {
            this.progressBarElement.disabled = this.disabled;
        }
        this.markersElement = this.querySelector('.timeline_markers');
        this.currentTimeElement = this.querySelector('.time_now');
        this.durationElement = this.querySelector('.duration');

        if (!this.debouncedSeek) {
            this.debouncedSeek = app.rateLimiter.debounce(this.seekToPending, 100);
        }
        this.progressBarElement?.addEventListener('input', this.seekFromInput);
        this.progressBarElement?.addEventListener('input', this.updateProgressFromInput);
    }

    disconnectedCallback(): void {
        this.progressBarElement?.removeEventListener('input', this.seekFromInput);
        this.progressBarElement?.removeEventListener('input', this.updateProgressFromInput);
        if (this.thumbnailListenersRegistered) {
            this.progressBarElement?.removeEventListener('mousemove', this.showThumbnailFromPointer);
            this.progressBarElement?.removeEventListener('mouseleave', this.hideThumbnail);
        }
    }

    getProgressBarElement(): HTMLInputElement|null {
        return this.progressBarElement;
    }

    setDisabled(disabled: boolean): void {
        if (this.progressBarElement) {
            this.progressBarElement.disabled = disabled;
        }
        this.disabled = disabled;
    }

    setDurationDisplay(show: boolean): void {
        if (!this.durationElement) {
            return;
        }

        this.showDuration = show;
        this.durationElement.textContent = show ? this.formatTime(this._endTime) : '--:--';
    }

    registerThumbnailPreview(): void {
        this.progressBarElement?.addEventListener('mousemove', this.showThumbnailFromPointer);
        this.progressBarElement?.addEventListener('mouseleave', this.hideThumbnail);
        this.thumbnailListenersRegistered = true;
    }

    setAdBreaks(breaks: Array<AdBreak>): void {
        this.adBreaks = breaks;

        if (!this.markersElement) {
            return;
        }

        this.markersElement.innerHTML = breaks
            .map(b => '<i class="timeline_marker' + (b.played ? ' timeline_marker--played' : '') + '"></i>')
            .join('');

        this.renderAdBreaks();
    }

    private renderAdBreaks(): void {
        if (!this.markersElement) {
            return;
        }

        const range = this._endTime - this._startTime;
        const markers = Array.from(this.markersElement.children) as Array<HTMLElement>;

        markers.forEach((marker, i) => {
            const time = this.adBreaks[i]?.time ?? 0;
            const visible = range > 0 && time > this._startTime && time < this._endTime;

            marker.hidden = !visible;
            marker.style.left = visible ? (100 * (time - this._startTime) / range) + '%' : '';
        });
    }

    registerSeekHandler(handler: (time: number) => void): void {
        this.seekHandler = handler;
    }

    setRange(startTime: number, currentTime: number, endTime: number): void {
        this._startTime = this.normaliseTime(startTime);
        this._endTime = Math.max(this._startTime, this.normaliseTime(endTime));
        this._currentTime = Math.max(
            this._startTime,
            Math.min(this.normaliseTime(this.pendingSeekTime ?? currentTime), this._endTime)
        );

        this.render();
    }

    private render(): void {
        if (!this.progressBarElement || !this.currentTimeElement || !this.durationElement) {
            return;
        }

        const range = this._endTime - this._startTime;
        const progress = range > 0
            ? (this._currentTime - this._startTime) / range
            : 0;

        this.progressBarElement.min = String(this._startTime);
        this.progressBarElement.max = String(this._endTime);
        this.progressBarElement.value = String(this._currentTime);

        this.progressBarElement.style.setProperty(
            '--timeline-percent',
            `${progress * 100}%`
        );

        this.currentTimeElement.textContent = this.formatTime(this._currentTime);

        this.renderAdBreaks();

        if (this.showDuration) {
            this.durationElement.textContent = this.formatTime(this._endTime);
        }
    }

    private normaliseTime(value: number): number {
        return Math.max(0, value) || 0;
    }

    private formatTime(value: number): string {
        const time = Math.floor(value);
        const hours = Math.floor(time / 3600);
        const minutes = Math.floor((time % 3600) / 60);
        const seconds = time % 60;

        if (hours > 0) {
            return hours + ':' + this.pad2(minutes) + ':' + this.pad2(seconds);
        }

        return this.pad2(minutes) + ':' + this.pad2(seconds);
    }

    private pad2(n: number): string {
        return ('0' + n).slice(-2);
    } 

    // This handles click and dragging of the timeline progress bar.
    private seekFromInput = (): void => {
        if (this.disabled || !this.progressBarElement) {
            return;
        }

        this.pendingSeekTime = Number(this.progressBarElement.value);
        this.debouncedSeek?.();
    };

    // Seeks to the position the user clicked or dragged to, runs debounced. 
    // seekFromInput sets where user wants to seek and this function does the seeking.
    private seekToPending = (): void => {
        const time = this.pendingSeekTime;
        // Cleared before seeking: the seek redraws with setRange, which must then draw the real playhead.
        this.pendingSeekTime = null;

        if (time !== null) {
            this.seekHandler(time);
        }
    };

    // This handles updating the progress bar and current time display when the user is dragging the timeline progress bar.
    // So we give immediate feedback to the user, even if the actual seek is throttled.
    private updateProgressFromInput = (): void => {
        if (this.disabled || !this.progressBarElement || !this.currentTimeElement) {
            return;
        }

        const time = Number(this.progressBarElement.value);
        const range = this._endTime - this._startTime;

        const progress = range > 0
            ? (time - this._startTime) / range
            : 0;

        this.progressBarElement.style.setProperty('--timeline-percent', progress * 100 + '%');

        this.currentTimeElement.textContent = this.formatTime(time);
    };

    private showThumbnailFromPointer = (event: MouseEvent): void => {
        if (!this.progressBarElement) {
            return;
        }
    
        const bounds = this.progressBarElement.getBoundingClientRect();
        const styles = getComputedStyle(this.progressBarElement);
        const thumbWidth =
            Number.parseFloat(styles.getPropertyValue('--timeline-thumb-width')) || 16;
    
        const trackStart = bounds.left + thumbWidth / 2;
        const trackWidth = bounds.width - thumbWidth;
    
        const progress = Math.max(
            0,
            Math.min((event.clientX - trackStart) / trackWidth, 1)
        );
    
        const time =
            this._startTime +
            (this._endTime - this._startTime) * progress;

        const percentage = Math.min(Math.max(event.clientX - bounds.left, 0), bounds.width) / bounds.width;
    
        app.voyoVideo.showThumbnailPreview(Math.round(time), percentage);
    };

    private hideThumbnail = (): void => {
        app.voyoVideo.hideThumbnailPreview();
    };
}

export default VoyoTimeline;
